'use client';

import style from './PageClient.module.scss';
import { useState, useEffect, useCallback } from 'react';
import Search from '@/components/ui/search/Search';
import CardView from '@/components/ui/cardView/CardView';
import Avatar from '@/components/ui/avatar/Avatar';
import Coin from '@/components/ui/coin/Coin';
import Button from '@/components/ui/button/submit/Button';
import Link from 'next/link';
import { IconDelete, IconCoinOutline } from '@/components/icons';
import { WalletService } from '@/libs/services/wallet';
import { showApiSuccess, showApiError } from '@/libs/helpers/showApiState';
import Image from 'next/image';
import { CoinData } from '@/types/wallet';
import { v4 as uuid } from 'uuid';
import { useListSearch } from '@/hooks/useListSearch';
import { useAppDispatch } from '@/store/hook';
import { startGlobalLoading, stopGlobalLoading } from '@/features/ui/uiLoadingSlice';
import { Skeleton } from 'antd';
import { useGlobalSkeletonLoading } from '@/hooks/useGlobalSkeletonLoading';
import { EmptyState } from '@/components/ui/emptyState';

type DeleteCoinItem = {
  type: string;
  type_id: string;
};

type GreenCoinCollectListProps = {
  onReloadWallet: () => Promise<void>;
};

const GreenCoinCollectList = ({ onReloadWallet }: GreenCoinCollectListProps) => {
  const isLoading = useGlobalSkeletonLoading(1000);
  const [greenCoinsListData, setGreenCoinsListData] = useState<CoinData[] | null>(null);
  const hasNoData = greenCoinsListData?.length === 0;
  const { keyword, setKeyword, filtered } = useListSearch<CoinData>({
    source: greenCoinsListData,
    searchBy: (coin) => coin.name ?? '', // 避免 null 問題
  });

  const [isDeletable, setIsDeletable] = useState(false);
  const [deleteCoinsList, setDeleteCoinsList] = useState<DeleteCoinItem[]>([]);
  const dispatch = useAppDispatch();

  const getCoinsListInfo = useCallback(async () => {
    dispatch(startGlobalLoading());
    try {
      const result = await WalletService.getGreenCoinsList();
      const resultWithId = result.map((r) => ({
        ...r,
        _key: uuid(),
      }));

      setGreenCoinsListData(resultWithId);
    } catch (err) {
      showApiError(err, '取得籌碼包資訊失敗');
    } finally {
      dispatch(stopGlobalLoading());
    }
  }, [dispatch]);

  const handleDeleteCoins = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked, dataset } = e.target;
    const type = dataset.type;
    const type_id = dataset.typeId;
    if (!type || !type_id) return;

    setDeleteCoinsList((prev) => {
      if (checked) {
        if (prev.some((item) => item.type === type && item.type_id === type_id)) {
          return prev;
        }
        return [...prev, { type, type_id }];
      } else {
        return prev.filter((item) => !(item.type === type && item.type_id === type_id));
      }
    });
  };

  const handleSubmitDelete = async () => {
    const type = deleteCoinsList.map((item) => {
      if (item.type === 'store') {
        return 'coin';
      } else {
        return item.type;
      }
    });
    const type_id = deleteCoinsList.map((item) => item.type_id);
    const payload = { type, type_id };
    dispatch(startGlobalLoading());
    try {
      await WalletService.deleteCoins(payload);
      showApiSuccess('成功刪除籌碼');
      await getCoinsListInfo();
      await onReloadWallet();
      setDeleteCoinsList([]);
      setIsDeletable(false);
    } catch (err) {
      showApiError(err, '刪除籌碼失敗');
    } finally {
      dispatch(stopGlobalLoading());
    }
  };

  useEffect(() => {
    getCoinsListInfo();
  }, [getCoinsListInfo]);

  return (
    <>
      {!hasNoData ? (
        <CardView>
          {/* 功能按鈕區 */}
          <div className="d-flex justify-content-end mb-10">
            {isDeletable && (
              <div className="d-flex align-items-center gap-2">
                <Button color="dark" size="sm" onClick={handleSubmitDelete}>
                  丟掉籌碼
                </Button>

                <Button
                  color="light"
                  size="sm"
                  onClick={() => {
                    setDeleteCoinsList([]);
                    setIsDeletable(false);
                  }}
                >
                  取消
                </Button>
              </div>
            )}

            {!isDeletable && (
              <div className="d-flex align-items-center gap-3">
                <Search value={keyword} onChange={setKeyword} />

                {/* <button type="button" className="px-2 line-height-none">
              <IconFilter width={22} />
            </button> */}
                <button
                  type="button"
                  className="line-height-none"
                  onClick={() => {
                    setIsDeletable(true);
                  }}
                >
                  <IconDelete width={22} />
                </button>
              </div>
            )}
          </div>

          {/* 列表區 */}
          <div className="row gy-10">
            {!isLoading
              ? (filtered ?? [])?.map((coin) => (
                  <div
                    key={coin._key}
                    className="col-4 col-md-3 col-lg-3 d-flex flex-column align-items-center"
                  >
                    {isDeletable && (
                      <div>
                        <input
                          type="checkbox"
                          className="big-checkbox"
                          id={coin.open_id}
                          data-type={coin.type}
                          data-type-id={coin.type_id}
                          onChange={(e) => handleDeleteCoins(e)}
                        />
                      </div>
                    )}
                    <Link
                      href={
                        isDeletable
                          ? '#'
                          : coin.type !== 'store'
                            ? `/user/${coin.open_id}`
                            : `/store/${coin.open_id}`
                      }
                      className={`position-relative ${isDeletable ? style.linkDisabled : ''}`}
                    >
                      <Coin color="green" scale={0.9}>
                        <Avatar src={coin.photo} />
                      </Coin>
                      <div className="text-center">
                        <span className={style.numTag} style={{ color: '#00b0b4' }}>
                          {coin.total}
                        </span>
                      </div>
                      {coin.emoji_photo && (
                        <span className="position-absolute top-0 start-0">
                          <Image
                            src={coin.emoji_photo}
                            alt={coin.emoji_id}
                            width={28}
                            height={28}
                          />
                        </span>
                      )}
                    </Link>

                    <span className="mb-2 text-truncate w-100 text-center fs-sm-1">
                      {coin.name}
                    </span>
                    {!isDeletable ? (
                      <button
                        type="button"
                        className={`text-truncate ${style.badge}`}
                        style={{
                          backgroundColor: `${
                            coin.is_bday ? '#EB76A1' : coin.is_new_coin ? '#FF9149' : '#1E9FF2'
                          }`,
                        }}
                      >
                        {coin.is_bday ? '本週壽星' : ''}
                      </button>
                    ) : (
                      ''
                    )}
                  </div>
                ))
              : Array.from({ length: 9 }).map((_, i) => (
                  <div key={i} className="col-4 col-md-3 col-lg-3">
                    <Skeleton.Avatar active size={100} shape="circle" />
                  </div>
                ))}
          </div>
        </CardView>
      ) : (
        <EmptyState title="目前尚未持有綠籌碼" icon={<IconCoinOutline width={36} />} />
      )}
    </>
  );
};

export default GreenCoinCollectList;
